import { useState } from 'react'

import type { ContextMenuOptionType } from '@inno-ui/context-menu'
import { GlobeLineIcon, MicrophoneLineIcon } from '@inno-ui/icons'
import { useTranslation } from 'react-i18next'
import { LANGUAGES } from 'shared/constants/Languages'

const LANGUAGE_OPTIONS = LANGUAGES.map(({ value, label }) => ({ value, label }))

const getLanguageLabel = (language: string | undefined) => {
  if (!language) return ''
  const opt = LANGUAGES.find(o => o.value === language)
  return opt ? opt.label : ''
}

export const useSttMenuOptions = (
  restoreFocus: () => void,
  setIsContextMenuOpen: (v: boolean) => void
) => {
  const { t } = useTranslation('translation', { keyPrefix: 'stt' })
  const [language, setLanguage] = useState<string | undefined>()
  const [targetLanguage, setTargetLanguage] = useState<string | undefined>()

  const makeOption = (
    icon: ContextMenuOptionType['icon'],
    label: string,
    items: { value: string; label: string }[],
    selected: string | undefined,
    onChange: (code: string | undefined) => void
  ): ContextMenuOptionType => ({
    icon,
    label,
    dropdownListProps: {
      items,
      value: items.find(o => o.value === (selected ?? '')),
      isSearchable: true,
      searchPlaceholder: t('search_language_placeholder'),
      onChange: val => {
        const opt = val as { value: string } | undefined
        onChange(opt?.value || undefined)
        setIsContextMenuOpen(false)
        restoreFocus()
      },
      maxHeight: '300px',
      onClose: () => {}
    }
  })

  const sourceLabel = getLanguageLabel(language)
  const targetLabel = getLanguageLabel(targetLanguage)

  const menuOptions: ContextMenuOptionType[] = [
    makeOption(
      MicrophoneLineIcon,
      `${t('source_language')}${sourceLabel ? ` (${sourceLabel})` : ''}`,
      [{ value: '', label: t('auto_detect') }, ...LANGUAGE_OPTIONS],
      language,
      setLanguage
    ),
    makeOption(
      GlobeLineIcon,
      `${t('target_language')}${targetLabel ? ` (${targetLabel})` : ''}`,
      [{ value: '', label: t('none') }, ...LANGUAGE_OPTIONS],
      targetLanguage,
      setTargetLanguage
    )
  ]

  return { language, targetLanguage, menuOptions }
}
